
import Component from "vue-class-component";
import Vue, { CreateElement } from "vue";
import { VDataTable } from "vuetify/lib";
import { Prop, Watch } from "vue-property-decorator";
import { EventPayloadHideCol, TableHeader, TableHeaders, TableItem, TableItems } from "@/types";
import { WatchParentResize } from "@/lib/helpers/customDecorators";
import { ScopedSlotChildren, VNode } from "vue/types/vnode";
import { isEqual } from "lodash-es";
import { TEXT } from "@/lib/const";
import { $t } from "@/lib/helpers/i18nHelper";
import { VRow, VCol } from "vuetify/lib";

@WatchParentResize("onResize")
@Component({ name: "BaseTable", components: { VRow, VCol } })
export default class BaseTable extends Vue {
  @Prop({ type: String, required: true })
  public readonly itemKey!: string;

  @Prop({ type: Array })
  public readonly headers!: TableHeaders;

  @Prop({ type: Array, required: true })
  public readonly items!: TableItems;

  public containerWidth: number = 0;

  public allHeaders: TableHeaders = [];

  public resultHeaders: TableHeaders = [];

  get hiddenHeaders(): TableHeaders {
    return this.allHeaders.filter(h => this.resultHeaders.indexOf(h) === -1);
  }

  get hasHiddenRows(): boolean {
    return this.hiddenHeaders.length > 0;
  }

  @Watch("hiddenHeaders", { immediate: true })
  public sendEvent(current: TableHeaders, previous: TableHeaders): void {
    if (!isEqual(current, previous)) {
      const payload: EventPayloadHideCol = {
        allHeaders: this.allHeaders,
        hiddenHeaders: this.hiddenHeaders,
        resultHeaders: this.resultHeaders,
      };
      this.$emit("hideCol", payload);
    }
  }

  public onResize(width: number): void {
    this.containerWidth = width;
    this.allHeaders = this.fillHeaders();
    this.resultHeaders = this.filterHeaders(this.allHeaders);
    if (this.hasHiddenRows) {
      this.resultHeaders = [
        ...this.resultHeaders,
        {
          value: "more",
          text: $t("More"),
          sortable: false,
          align: "left",
          width: "82",
        },
      ];
    }
  }

  public filterHeaders(headers: TableHeaders): TableHeaders {
    const checkRules = (rules: number[], currentWidth: number): boolean => {
      for (const rule of rules) {
        if (rule >= currentWidth) {
          return false;
        }
      }
      return true;
    };

    return headers.filter(header => {
      if (header.hideRules) {
        return checkRules(header.hideRules, this.containerWidth);
      } else return true;
    });
  }

  public fillHeaders(): TableHeaders {
    if (this.headers) return this.headers;

    if (!this.$scopedSlots.h) return [];
    const nodes: ScopedSlotChildren = this.$scopedSlots.h({});
    if (!nodes) return [];

    let result: TableHeaders = [];

    const rulesToPixelPoints = (stringRules: string[]): number[] => {
      const thresholds = (this.$vuetify.breakpoint.thresholds as any) as {
        [k: string]: number;
      };
      return stringRules
        .map(value => {
          const rule = value.split("-")[1];
          if (!isNaN(Number(rule))) return Number(rule);
          if (Reflect.has(thresholds, rule)) return thresholds[rule];
          return -1;
        })
        .filter(i => i > -1);
    };

    nodes.forEach(node => {
      if (!node.data || !node.data.attrs) return;
      const stringHideRules: string[] = (Object.keys(node.data.attrs) as string[]).filter((k: string) =>
        k.match(/hide-.*/)
      );

      const hideRules = rulesToPixelPoints(stringHideRules);
      const value = node.data.attrs.value;
      const text = node.children && node.children.length > 0 ? node.children[0].text : value;
      const align = node.data.attrs.align;
      const sortable = node.data.attrs.sortable || false;
      const width = typeof node.data.attrs.width === "number" ? `${node.data.attrs.width}px` : node.data.attrs.width;
      const clazz = node.data.attrs.class;

      const th: TableHeader = {
        text,
        value,
        align,
        sortable,
        width,
        class: clazz,
        hideRules,
      };
      result.push(th);
    });

    return result;
  }

  private render(h: CreateElement): VNode {
    interface SlotProps {
      item: TableItem;
      expand: (v: boolean) => void;
      isExpanded: boolean;
    }

    if (this.items.length === 0) {
      return h("div", { class: { "subheading": true, "pt-3": true } }, [$t("No matching results found")]);
    }

    const getCol = (props: SlotProps, key: string, content: string | VNode[], clazz: string = ""): VNode[] => {
      const slotFunc = this.$scopedSlots[`col-${key}`];
      const slot = slotFunc ? slotFunc(props) : undefined;
      return [h("td", { class: clazz }, slot ? slot : content)];
    };

    const itemsSlot: (props: SlotProps) => VNode[][] = props => {
      const slots: VNode[][] = [];

      this.resultHeaders.forEach(header => {
        const colData = props.item[header.value];
        if (header.value === "more") return;
        let content = "";
        if (colData !== null) {
          content = colData ? colData.toString() : "";
        }
        slots.push(getCol(props, header.value, content));
      });

      const moreSlot = getCol(props, "more", [
        h(
          "a",
          {
            class: "buttonMore",
            on: { click: () => props.expand(!props.isExpanded) },
          },
          props.isExpanded ? $t("Hide") : $t("Show")
        ),
      ]);

      if (this.hasHiddenRows) {
        slots.push(moreSlot);
      }

      return slots;
    };

    const defaultExpandSlot: (props: SlotProps) => VNode[] = props => {
      const valueItems: VNode[] = [];
      this.hiddenHeaders.forEach(header => {
        const textData = props.item[header.value];
        const text = textData ? textData.toString() : TEXT.FIELD_EMPTY;
        const slotFunc = this.$scopedSlots[`expanded-col-${header.value}`] || this.$scopedSlots[`col-${header.value}`];
        const isExpanded = !!this.$scopedSlots[`expanded-col-${header.value}`];
        const value = slotFunc ? slotFunc(props) : text;
        const defaultContent = [
          h("div", { class: "font-weight-bold" }, $t(header.text)),
          h("div", { class: "body-2" }, value),
        ];
        const item = h("v-col", { class: { "col-12": true, "pb-2": true } }, isExpanded ? value : defaultContent);
        // check: if has expanded slot but does not have value for it, will not add item.
        // So there won't be empty blocks with padding
        if (!isExpanded || value !== undefined) {
          valueItems.push(item);
        }
      });

      const expandedBody = [h("v-row", { ref: "expanded", class: "expanded__content" }, valueItems)];
      const result = h("tr", [h("td", { attrs: { colspan: this.resultHeaders.length } }, expandedBody)]);
      return valueItems.length !== 0 ? [result] : [];
    };
    return h(VDataTable, {
      props: {
        items: this.items,
        itemsPerPage: this.items.length,
        headers: this.resultHeaders,
        hideDefaultFooter: true,
        itemKey: this.itemKey,
        expand: true,
        mobileBreakpoint: Number.NEGATIVE_INFINITY,
      },
      scopedSlots: {
        "item": (props: SlotProps) => {
          return [
            h(
              "tr",
              { class: { "expanded expanded__row": props.isExpanded && this.hiddenHeaders.length > 0 } },
              itemsSlot(props)
            ),
          ];
        },
        "expanded-item": defaultExpandSlot,
        ...this.$scopedSlots,
      },
    });
  }
}
